import { memo, useCallback, useContext } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import type { MultipleBarChart } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { trunc } from '@trinity/chat-components/utils';
import { formatValueWithUnit } from '@trinity/chat-components/utils/chart';
import { ChartHoverText } from './Hover';
import { replaceTemplateValues } from './template-utils';
import { TemplateTooltip } from './TemplateTooltip';
import { theme } from './theme';
import { formatAxisLabel } from './utils';

type Props = {
  chart: MultipleBarChart;
  colors: string[];
  isStacked: boolean;
  animate?: boolean;
  seriesToN?: Record<string, number>;
  unit?: string;
  isPrefix?: boolean;
  isInteractive?: boolean;
};

type ChartDatum = Record<string, string | number | undefined>;

export const NivoMultipleBarChart = memo(
  ({ chart, colors, isStacked, animate = false, seriesToN, unit, isPrefix, isInteractive = true }: Props) => {
    const returnedData = chart.data;
    const items: ChartDatum[] = [];
    const keysSet = new Set<string>();
    const colorCount = colors.length;

    for (let i = 0; i < returnedData.length; i++) {
      const series = returnedData[i];
      if (!series || !Array.isArray(series.data) || series.data.length === 0) {
        continue;
      }

      const bar: ChartDatum = {};

      bar[chart.indexBy] = series.index;
      const seriesName = series.index;
      const seriesN = seriesToN?.[seriesName];

      for (let j = 0; j < series.data.length; j++) {
        const point = series.data[j];
        if (!point) {
          continue;
        }

        const category = point.x;
        keysSet.add(category);
        bar[category] = point.y;
        const colorIndex = colorCount > 0 ? j % colorCount : 0;
        bar[`${category}Color`] = colors[colorIndex] ?? '#999999';

        if (seriesN !== undefined) {
          bar[`${category}_columnN`] = seriesN;
        }
      }

      items.push(bar);
    }

    items.reverse();

    const keys = Array.from(keysSet);

    const [, setOpen] = useContext(LegendCollapseToggleContext);

    const formatLeftAxisLabel = useCallback((value: string) => {
      return trunc(value, 10);
    }, []);

    const formatBottomAxisLabel = useCallback(
      (value: string) => {
        return formatAxisLabel({ value, unit, isPrefix });
      },
      [unit, isPrefix]
    );

    return (
      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
        <ResponsiveBar
          data={items}
          groupMode={isStacked ? 'stacked' : 'grouped'}
          keys={keys}
          indexBy={chart.indexBy}
          margin={{ top: 10, right: 20, bottom: 50, left: 80 }}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={colors}
          enableGridY={false}
          isInteractive={isInteractive}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 1.6]],
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: chart.yAxisTitle,
            legendPosition: 'middle',
            legendOffset: 32,
            truncateTickAt: 20,
            format: formatBottomAxisLabel,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            // legend: props.chart.yAxisTitle,
            legendPosition: 'middle',
            format: formatLeftAxisLabel,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor="#ffffff"
          label={(d) => formatValueWithUnit({ value: d.value, unit, isPrefix })}
          layout="horizontal"
          tooltip={
            isInteractive
              ? (e) => {
                  // Get the columnN for this specific bar's category (e.id is the key/category)
                  const rawColumnN = (e.data as Record<string, unknown>)[`${e.id}_columnN`];
                  const columnNForBar = typeof rawColumnN === 'number' ? rawColumnN : undefined;
                  // If a tooltip template is provided, use it
                  if (chart.tooltipTemplate) {
                    const seriesKey = String(e.id);
                    const seriesExtra = chart.seriesData?.[seriesKey] ?? {};
                    const templateValues = {
                      xAxisLabel: e.indexValue,
                      xValue: e.indexValue,
                      seriesId: e.id,
                      seriesLabel: String(e.id),
                      yValue: e.value,
                      category: e.id,
                      xAxisTitle: chart.xAxisTitle,
                      yAxisTitle: chart.yAxisTitle,
                      value: e.value,
                      label: e.label,
                      unit: unit || '',
                      columnN: columnNForBar,
                      ...seriesExtra,
                    };
                    const content = replaceTemplateValues(chart.tooltipTemplate, templateValues);
                    return <TemplateTooltip content={content} />;
                  }
                  // Otherwise use the default tooltip
                  return (
                    <ChartHoverText
                      name={e.label}
                      count={e.value}
                      columnN={columnNForBar}
                      unit={unit}
                      isPrefix={isPrefix}
                    />
                  );
                }
              : undefined
          }
          onClick={
            isInteractive
              ? (data) => {
                  setOpen(true);
                }
              : undefined
          }
          animate={animate}
          theme={theme}
          role="application"
        />
      </div>
    );
  }
);
